import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { ChevronRight, X } from 'lucide-react';
import { stayService } from '../../lib/stay/stayService';
import { analyticsService } from '../../lib/analytics/analyticsService';
import { useTranslatedContent } from '../../hooks/useTranslatedContent';
import "react-datepicker/dist/react-datepicker.css";
import "../../styles/calendar.css";
import { registerLocale } from 'react-datepicker';
import fr from 'date-fns/locale/fr';
import enUS from 'date-fns/locale/en-US';
import es from 'date-fns/locale/es';
import it from 'date-fns/locale/it';
import de from 'date-fns/locale/de';
import nl from 'date-fns/locale/nl';
import ja from 'date-fns/locale/ja';
import zhCN from 'date-fns/locale/zh-CN';
import ru from 'date-fns/locale/ru';
import pt from 'date-fns/locale/pt';
// Register all locales
registerLocale('fr', fr);
registerLocale('en', enUS);
registerLocale('es', es);
registerLocale('it', it);
registerLocale('de', de);
registerLocale('nl', nl);
registerLocale('ja', ja);
registerLocale('zh', zhCN);
registerLocale('ru', ru);
registerLocale('pt', pt);

interface DateRangeSelectorProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  onSearch: () => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  isModalOpen?: boolean;
  setIsModalOpen?: (isOpen: boolean) => void;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onSearch,
  config,
  translations,
  isModalOpen = true,
  setIsModalOpen
}) => {
  const { i18n } = useTranslation();
  const [localIsModalOpen, setLocalIsModalOpen] = useState(isModalOpen);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarType, setCalendarType] = useState<'start' | 'end' | null>(null);
  const [datesInitialized, setDatesInitialized] = useState(false);
  const [areDatesExpired, setAreDatesExpired] = useState(false);

  // Use the prop value if provided, otherwise use local state
  const effectiveIsModalOpen = setIsModalOpen ? isModalOpen : localIsModalOpen;
  const setEffectiveIsModalOpen = setIsModalOpen || setLocalIsModalOpen;

  // Get translations for the component
  const { translations: pageTranslations } = useTranslatedContent({
    terms: [
      'stay_dates', 
      'stay_events',
      'stay_events_modify',
      'start_date', 
      'end_date', 
      'select_dates', 
      'validate', 
      'stay_helptext',
      'stay_helptext_new',
      'back',
      'search'
    ]
  });

  // Initialize dates from service or defaults only once
  useEffect(() => {
    if (datesInitialized) {
      return; // Skip if already initialized
    }

    // Check if dates are expired
    const datesExpired = stayService.areDatesExpired();
    setAreDatesExpired(datesExpired);

    // Check if we have saved stay dates
    const savedDates = stayService.getStayDates();
    
    if (savedDates && !datesExpired) {
      // Use saved dates if available and not expired
      onStartDateChange(new Date(savedDates.startDate));
      onEndDateChange(new Date(savedDates.endDate));
    } else {
      // Otherwise use defaults
      const { startDate: defaultStart, endDate: defaultEnd } = stayService.getDefaultDates();
      onStartDateChange(defaultStart);
      onEndDateChange(defaultEnd);
    }
    
    setDatesInitialized(true);
  }, [startDate, endDate, onStartDateChange, onEndDateChange, datesInitialized]);

  // Set CSS variables for calendar colors
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--calendar-primary-color', config['major-color']);
    root.style.setProperty('--calendar-text-color', config['dark-color']);
    root.style.setProperty('--calendar-secondary-color', config['medium-color']);
    root.style.setProperty('--calendar-background-color', '#ffffff');
  }, [config]);

  // Map i18n language to date-fns locale
  const getDatePickerLocale = () => {
    const localeMap: Record<string, string> = {
      'fr': 'fr',
      'en': 'en',
      'es': 'es',
      'it': 'it',
      'pt': 'pt',
      'de': 'de',
      'nl': 'nl',
      'ja': 'ja',
      'zh': 'zh',
      'ru': 'ru'
    };
    
    return localeMap[i18n.language] || 'fr';
  };

  const getTranslatedText = (key: string): string => {
    // First try to get from pageTranslations
    if (pageTranslations && pageTranslations[key]) {
      return pageTranslations[key][i18n.language] || pageTranslations[key].fr || key;
    }
    
    // Then try from passed translations
    const translation = translations[key];
    if (!translation) return key;
    return translation[i18n.language] || translation.fr || key;
  };

  const handleSearch = () => {
    if (!startDate || !endDate) return;
    
    // Save the dates to the stay service
    stayService.setStayDates(startDate, endDate);
    
    // Track the event
    analyticsService.trackEvent({
      event_type: 'filter',
      event_name: 'date_filter',
      event_data: {
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      }
    });
    
    // Call the search function
    onSearch();
    
    // Close the modal after search to show compact view
    setEffectiveIsModalOpen(false);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (showCalendar) {
        setShowCalendar(false);
        setCalendarType(null);
      } else {
        // Always allow closing the modal
        setEffectiveIsModalOpen(false);
      }
    }
  };

  const handleCloseClick = () => {
    // Always allow closing the modal
    setEffectiveIsModalOpen(false);
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return '';

    const day = date.getDate();
    const month = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(date);
    
    // Adapt format based on language
    switch (i18n.language) {
      case 'en':
        return `${month} ${day}`;
      case 'ja':
      case 'zh':
        return `${month}${day}日`;
      default:
        return `${day} ${month.toLowerCase()}`;
    }
  };

  // Function to style days in the calendar
  const dayClassName = (date: Date) => {
    if (!startDate || !endDate) return '';

    const isInRange = date >= startDate && date <= endDate;
    const isStartDate = startDate && date.toDateString() === startDate.toDateString();
    const isEndDate = endDate && date.toDateString() === endDate.toDateString();

    let classes = 'relative transition-colors duration-200 ';

    if (isStartDate || isEndDate) {
      classes += `text-white before:absolute before:inset-0 before:rounded-full before:bg-[${config['major-color']}] before:-z-[1]`;
    } else if (isInRange) {
      classes += `text-white before:absolute before:inset-0 before:bg-[${config['major-color']}] before:-z-[1]`;
    }

    return classes;
  };

  const handleDateSelect = (date: Date | null) => {
    if (!calendarType) return;

    if (calendarType === 'start') {
      onStartDateChange(date);
      // If end date exists and is before the new start date
      if (endDate && date && date > endDate) {
        // Set end date to be one day after the new start date
        const newEndDate = new Date(date);
        newEndDate.setDate(newEndDate.getDate() + 1);
        newEndDate.setHours(23, 59, 59);
        onEndDateChange(newEndDate);
      }
    } else {
      onEndDateChange(date);
    }

    setShowCalendar(false);
    setCalendarType(null);
  };

  if (!effectiveIsModalOpen) {
    return (
      <button
        onClick={() => setEffectiveIsModalOpen(true)}
        className="fixed left-1/2 -translate-x-1/2 z-[150] bottom-[120px] bg-white rounded-full shadow-lg px-4 py-2 flex items-center gap-2 text-sm"
      >
        {startDate && endDate ? (
          <>
            <span>{formatDate(startDate)}</span>
            <ChevronRight size={16} className="text-gray-400" />
            <span>{formatDate(endDate)}</span>
          </>
        ) : (
          <span>{getTranslatedText('select_dates')}</span>
        )}
      </button>
    );
  }

  // Determine the title based on whether stay dates are already set
  const titleKey = stayService.hasStayDates() && !areDatesExpired ? 'stay_events_modify' : 'stay_dates';

  // Determine the help text key based on whether dates are expired
  const helpTextKey = areDatesExpired ? 'stay_helptext_new' : 'stay_helptext';
  
  // Get the help text
  const helpText = getTranslatedText(helpTextKey);
  
  // Format modal
  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-[400] flex items-center justify-center p-4"
      onClick={handleOverlayClick}
    >
      <div 
        className="bg-white w-full max-w-sm mx-auto relative overflow-hidden"
        style={{ borderRadius: config.border_radius }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h2 
            className="text-xl font-semibold"
            style={{ color: config['dark-color'] }}
          >
            {showCalendar ? (
              <button 
                onClick={() => {
                  setShowCalendar(false);
                  setCalendarType(null);
                }}
                className="flex items-center gap-2"
              >
                <ChevronRight size={20} className="rotate-180" />
                <span>{getTranslatedText('back')}</span>
              </button>
            ) : (
              getTranslatedText(titleKey)
            )}
          </h2>
          <button
            onClick={handleCloseClick}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {!showCalendar && (
            <p className="text-sm text-gray-600 mb-4">
              {helpText}
            </p>
          )}
          
          {showCalendar ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {getTranslatedText(calendarType === 'start' ? 'start_date' : 'end_date')}
              </label>
              <DatePicker
                selected={calendarType === 'start' ? startDate : endDate}
                onChange={handleDateSelect}
                selectsStart={calendarType === 'start'}
                selectsEnd={calendarType === 'end'}
                startDate={startDate}
                endDate={endDate}
                minDate={calendarType === 'start' ? new Date() : startDate}
                inline
                dayClassName={date => dayClassName(date)}
                calendarClassName="!border-0"
                locale={getDatePickerLocale()}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => {
                  setShowCalendar(true);
                  setCalendarType('start');
                }}
                className="p-4 border rounded-lg text-left hover:bg-gray-50 transition-colors"
              >
                <div className="text-sm text-gray-500 mb-1">
                  {getTranslatedText('start_date')}
                </div>
                <div className="font-medium">
                  {startDate ? formatDate(startDate) : getTranslatedText('select_dates')}
                </div>
              </button>

              <button
                onClick={() => {
                  setShowCalendar(true);
                  setCalendarType('end');
                }}
                className="p-4 border rounded-lg text-left hover:bg-gray-50 transition-colors"
              >
                <div className="text-sm text-gray-500 mb-1">
                  {getTranslatedText('end_date')}
                </div>
                <div className="font-medium">
                  {endDate ? formatDate(endDate) : getTranslatedText('select_dates')}
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Footer - Only show when not in calendar view */}
        {!showCalendar && (
          <div className="p-4 border-t">
            <button
              onClick={handleSearch}
              className="w-full px-4 py-2 text-white rounded-lg transition-colors"
              style={{ 
                backgroundColor: config['major-color'],
                borderRadius: config.border_radius 
              }}
              disabled={!startDate || !endDate}
            >
              {getTranslatedText('search')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangeSelector;